import React, { CSSProperties, useState } from 'react';
import styled, { css } from 'styled-components';
import { walletSizeMap } from './WalletPrice';
import { useExplorationPoint } from '@hooks/useExplorationPoint';
import { SettingCollectionPath } from '@typings/collections.enum';
import { useDocument } from '@nandorojo/swr-firestore';
import { IExplorationPointSetting } from '@typings/setting/explorationSetting';
import useWindowWidth from '@hooks/useWindowWidth';

const absoluteIconStyle = css`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  left: 8px;
`;

const PointIconImg = styled.img`
  object-fit: contain;
  flex-shrink: 0;
  max-height: 40px;
  ${absoluteIconStyle};

  @media (max-width: 480px) {
    max-width: 20px;
  }
`;

const MyExplorationPoint = styled.div<{
  size: keyof typeof walletSizeMap;
  $hasIcon: boolean;
}>`
  padding: 8px 12px;
  width: 100%;
  background-color: var(--subColor);
  color: var(--subTextColor);
  border: 1px solid var(--lineColor);
  corner-shape: var(--cardCornerShape);
  border-radius: 8px;
  font-size: 11px;
  font-weight: 800;
  position: relative;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  gap: 6px;
  ${({ $hasIcon }) =>
    $hasIcon &&
    css`
      padding-left: 36px;
    `};
  ${({ size }) => walletSizeMap[size]};
  cursor: pointer;

  & > * {
    z-index: 1;
  }

  @media (max-width: 480px) {
    font-size: 9px;
    justify-content: center;

    /* .exploration-point-text {
      display: none;
    } */
  }
`;

const TooltipBubble = styled.div`
  position: absolute;
  left: 50%;
  bottom: -28px;
  transform: translateX(-50%);
  background-color: var(--menuBackgroundColor);
  color: var(--menuTextColor);
  border: 1px solid var(--lineColor);
  border-radius: 6px;
  padding: 6px 8px;
  font-size: 10px;
  white-space: nowrap;
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.2);
`;

const ExplorationPointContainer = styled.div`
  width: 100%;
  text-align: center;

  @media (max-width: 480px) {
    width: fit-content;
    flex-shrink: 0;
  }
`;

type Props = {
  style?: CSSProperties;
  size?: keyof typeof walletSizeMap;
};

function ExplorationPoint({ style, size = 'sm' }: Props) {
  const { explorationPoint, formatTimeUntilNext, isActive, maxPoints } =
    useExplorationPoint();
  const { data } = useDocument<IExplorationPointSetting>(
    `${SettingCollectionPath.EXPLORATION}`,
  );
  const ww = useWindowWidth();
  const isMobile = (ww || 0) <= 480;
  const [showTip, setShowTip] = useState(false);

  // 탐색 포인트 시스템이 비활성화되어 있으면 컴포넌트를 렌더링하지 않음
  if (!isActive) {
    return null;
  }
  const pointIconURL = data?.pointIconURL || '';

  return (
    <MyExplorationPoint
      className="exploration-point"
      style={{
        ...style,
      }}
      size={size}
      $hasIcon={!!pointIconURL}
      onClick={() => {
        if (isMobile) setShowTip((v) => !v);
      }}
    >
      {pointIconURL && <PointIconImg src={pointIconURL} alt="탐색 포인트" />}
      <ExplorationPointContainer>
        <span className="exploration-point-text">탐색 포인트 &nbsp;&nbsp;</span>
        <strong>{explorationPoint}</strong>
        <span style={{ color: 'var(--subTextColor)' }}>/ {maxPoints}</span>
      </ExplorationPointContainer>
      {!isMobile && (
        <div
          style={{
            color: 'var(--subTextColor)',
            whiteSpace: 'nowrap',
            fontSize: '10px',
            opacity: 0.8,
          }}
        >
          {formatTimeUntilNext()}
        </div>
      )}
      {isMobile && showTip && (
        <TooltipBubble>{formatTimeUntilNext()}</TooltipBubble>
      )}
    </MyExplorationPoint>
  );
}

export default ExplorationPoint;
